import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { IonicApp, IonicErrorHandler, IonicModule } from 'ionic-angular';
import { HttpClientModule } from '@angular/common/http';
import { AgmCoreModule } from '@agm/core';
import { MomentModule } from 'angular2-moment';
import { DatePickerModule } from 'ionic-calendar-date-picker';
import { ImageCropperComponent } from 'ng2-img-cropper';
import { RouterModule, Routes } from '@angular/router';

// Pages
import { MyApp } from './app.component';
import { HomePage } from '../pages/home/home';
import { ListPage } from '../pages/list/list';
import { LoadingPage } from '../pages/loading/loading';
import { LoginPage } from '../pages/login/login';
import { RegisterChoosePage } from '../pages/register-choose/register-choose';
import { RegisterStaffPage } from '../pages/register-staff/register-staff';
import { RegisterRecruiterPage } from '../pages/register-recruiter/register-recruiter';
import { PrivacyPage } from '../pages/privacy/privacy';
import { TermsPage } from '../pages/terms/terms';
import { CreateJobPage } from './../pages/create-job/create-job';
import { CreateQuotePage } from './../pages/create-quote/create-quote';
import { MyJobsPage } from './../pages/my-jobs/my-jobs';
import { MyProfilePage } from './../pages/my-profile/my-profile';
import { SettingsPage } from './../pages/settings/settings';
import { PrivacySettingsPage } from './../pages/privacy-settings/privacy-settings';
import { LegalPage } from './../pages/legal/legal';
import { ProfileImagePage } from './../pages/profile-image/profile-image';
import { AccountsPage } from './../pages/accounts/accounts';
import { NotificationsPage } from './../pages/notifications/notifications';
import { JobPage } from './../pages/job/job';
import { BankDetailsPage } from './../pages/bank-details/bank-details';
import { TrainingCardPage } from './../pages/training-card/training-card';

import { MapModalPage } from '../pages/map-modal/map-modal';

// Shared Components
import { UserComponent } from '../shared/user/user';
import { RatingDisplayComponent } from '../shared/rating/rating-display';
import { RatingCreateComponent } from '../shared/rating/rating-create';
import { RatingForStaffTypeComponent } from '../shared/rating/rating-for-stafftype';
import { HeaderToolbarComponent } from '../shared/layout/header-toolbar';
import { JobInfoComponent } from './../shared/jobs/job-info';
import { UserInfoComponent } from './../shared/user/user-info';
import { PaymentComponent } from './../shared/payment/payment';
import { ProvinceSelectorComponent } from './../shared/province-selector/province-selector';
import { TrainingCardInfoComponent } from './../shared/training/training-card-info';

// Pipes
import { ReversePipe } from '../pipes/reverse/reverse';
import { SafeHtmlPipe } from '../pipes/safe-html/safe-html';

// Commented out for PWA
// import {StatusBar} from '@ionic-native/status-bar';
// import {SplashScreen} from '@ionic-native/splash-screen';

// Angular Fire
import { AngularFireModule } from 'angularfire2';
import { AngularFirestoreModule } from 'angularfire2/firestore';
// import {
//   AngularFireDatabaseModule,
//   AngularFireDatabase,
// } from 'angularfire2/database';
import { AngularFireAuthModule } from 'angularfire2/auth';

// Providers
import { AuthProvider } from '../providers/auth/auth';
import { WindowProvider } from '../providers/window/window';
import { GeolocationProvider } from '../providers/geolocation/geolocation';
import { JobsProvider } from '../providers/jobs/jobs';
import { StaffProvider } from '../providers/staff/staff';
import { NotificationsProvider } from './../providers/notifications/notifications';
import { RatingsProvider } from './../providers/ratings/ratings';
import { TrainingCardsProvider } from './../providers/training/training';
import { DistrictsProvider } from './../providers/districts/districts';

// PROD
export const firebaseConfig = {
  apiKey: 'AIzaSyC1xXPoqrO_W66uFvEmHcp4rHP6nb1832E',
  authDomain: 'joboxapps.firebaseapp.com',
  databaseURL: 'https://joboxapps.firebaseio.com',
  projectId: 'joboxapps',
  storageBucket: 'joboxapps.appspot.com',
  messagingSenderId: '818773942106',
  appId: '1:818773942106:web:1c28bf9b99228e64e49d63'
};

// DEV
// export const firebaseConfig = {
//   apiKey: 'AIzaSyCqVC7-QIZ0FmBf6DSRcrD3bzxZ8XCNIPw',
//   authDomain: 'jbx-app.firebaseapp.com',
//   databaseURL: 'https://jbx-app.firebaseio.com',
//   projectId: 'jbx-app',
//   storageBucket: 'jbx-app.appspot.com',
//   messagingSenderId: '616631668207'
// };

@NgModule({
  declarations: [
    MyApp,
    HomePage,
    ListPage,
    LoadingPage,
    LoginPage,
    RegisterChoosePage,
    RegisterRecruiterPage,
    RegisterStaffPage,
    PrivacyPage,
    TermsPage,
    CreateJobPage,
    CreateQuotePage,
    MapModalPage,
    MyJobsPage,
    MyProfilePage,
    SettingsPage,
    AccountsPage,
    JobPage,
    NotificationsPage,
    PrivacySettingsPage,
    LegalPage,
    ProfileImagePage,
    BankDetailsPage,
    TrainingCardPage,
    // Components
    UserComponent,
    RatingDisplayComponent,
    RatingCreateComponent,
    RatingForStaffTypeComponent,
    HeaderToolbarComponent,
    JobInfoComponent,
    UserInfoComponent,
    PaymentComponent,
    ImageCropperComponent,
    ProvinceSelectorComponent,
    TrainingCardInfoComponent,
    // Pipes
    ReversePipe,
    SafeHtmlPipe
  ],
  imports: [
    BrowserModule,
    IonicModule.forRoot(
      MyApp,
      {
        mode: 'md'
        // locationStrategy: 'path'
      },
      {
        links: [
          { component: HomePage, name: 'Dashboard', segment: '' },
          { component: JobPage, name: 'Job', segment: 'job/:item' },
          {
            component: CreateJobPage,
            name: 'Create Job',
            segment: 'create-job'
          },
          {
            component: CreateQuotePage,
            name: 'Create Quote',
            segment: 'create-quote'
          },
          { component: AccountsPage, name: 'Accounts', segment: 'accounts' },
          {
            component: NotificationsPage,
            name: 'Notifications',
            segment: 'notifications'
          },
          { component: MyJobsPage, name: 'My Jobs', segment: 'my-jobs' },
          {
            component: MyProfilePage,
            name: 'My Profile',
            segment: 'my-profile'
          },
          { component: SettingsPage, name: 'Settings', segment: 'settings' },
          {
            component: BankDetailsPage,
            name: 'Banking Details',
            segment: 'settings/banking-details'
          },
          {
            component: PrivacySettingsPage,
            name: 'Privacy Settings',
            segment: 'settings/privacy-settings'
          },
          { component: LegalPage, name: 'Legal', segment: 'legal' },
          {
            component: TermsPage,
            name: 'Terms and Conditions',
            segment: 'legal/terms'
          },
          {
            component: PrivacyPage,
            name: 'Privacy Policy',
            segment: 'legal/privacy'
          }
        ]
      }
    ),
    RouterModule.forRoot([]),
    HttpClientModule,
    AngularFireModule.initializeApp(firebaseConfig),
    AngularFirestoreModule,
    // AngularFireDatabaseModule,
    AngularFireAuthModule,
    AgmCoreModule.forRoot({
      apiKey: 'AIzaSyCqVC7-QIZ0FmBf6DSRcrD3bzxZ8XCNIPw',
      libraries: ['places']
    }),
    MomentModule,
    DatePickerModule
  ],
  bootstrap: [IonicApp],
  entryComponents: [
    MyApp,
    HomePage,
    ListPage,
    LoadingPage,
    LoginPage,
    RegisterChoosePage,
    RegisterRecruiterPage,
    RegisterStaffPage,
    PrivacyPage,
    TermsPage,
    MyJobsPage,
    CreateJobPage,
    CreateQuotePage,
    MapModalPage,
    MyProfilePage,
    SettingsPage,
    AccountsPage,
    JobPage,
    NotificationsPage,
    PrivacySettingsPage,
    LegalPage,
    ProfileImagePage,
    BankDetailsPage,
    TrainingCardPage,
    UserComponent,
    RatingDisplayComponent,
    RatingCreateComponent,
    RatingForStaffTypeComponent,
    HeaderToolbarComponent,
    JobInfoComponent,
    UserInfoComponent,
    PaymentComponent,
    ProvinceSelectorComponent,
    TrainingCardInfoComponent
  ],
  providers: [
    // StatusBar,
    // SplashScreen,
    AngularFirestoreModule,
    // AngularFireDatabase,
    { provide: ErrorHandler, useClass: IonicErrorHandler },
    AuthProvider,
    WindowProvider,
    GeolocationProvider,
    JobsProvider,
    StaffProvider,
    NotificationsProvider,
    RatingsProvider,
    TrainingCardsProvider,
    DistrictsProvider
  ]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts